import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "react-bootstrap";
import { InfoCard, SDashboard } from "./Dashboard.styles";
import { SButton } from "../BasicComponents";
import { isLectureLive } from "../../utils/lectureUtils";
import SyncLineChart from "../Charts/SyncLineChart";
import SyncDonutGraph from "../Charts/SyncDonutGraph";
import AsyncBarChart from "../Charts/AsyncBarChart";
import AsyncLineChart from "../Charts/AsyncLineChart";
import { SocketContext } from "../../context/SocketContext";

const Dashboard = ({ lecture, sidePanelVisible, visible }) => {
  const { t } = useTranslation("", { keyPrefix: "components.dashboard" });

  const { participants, globalUnderstanding, chatMessages, notes, bookmarks } = useContext(SocketContext);
  const [seeAsyncDashboard, setSeeAsyncDashboard] = useState(true);
  const [viewChapters, setViewChapters] = useState(true);
  /* const [questionIndex, setQuestionIndex] = useState(0);
  const questions = Object.values(chatMessages).filter((m) => m.isQuestion); */

  const currentPercentage = useMemo(() => {
    const total =
      globalUnderstanding.counters.low + globalUnderstanding.counters.mid + globalUnderstanding.counters.high;
    if (total === 0) return 0;
    const weightedTotal = globalUnderstanding.counters.high + 0.5 * globalUnderstanding.counters.mid;
    return (weightedTotal / total) * 100;
  }, [globalUnderstanding.counters.high, globalUnderstanding.counters.low, globalUnderstanding.counters.mid]);

  /* const prevClass = questionIndex > 0 ? "arrow-active" : "arrow-disabled";
  const nextClass = questionIndex < questions.length - 1 ? "arrow-active" : "arrow-disabled";

  const prevQuestion = () => {
    if (questionIndex > 0) setQuestionIndex(questionIndex - 1);
  };

  const nextQuestion = () => {
    if (questionIndex < questions.length - 1) setQuestionIndex((i) => i + 1);
  }; */

  return (
    <SDashboard sidePanelVisible={sidePanelVisible} visible={visible}>
      <div className="title-section">
        <h1 className="mx-2 text-uppercase">{t("title")}</h1>
        <i className="fas fa-chart-bar fa-2x" />
      </div>
      {lecture.type === "live" && !isLectureLive(lecture) && (
        <SButton className="change-stats" onClick={() => setSeeAsyncDashboard((v) => !v)}>
          {seeAsyncDashboard ? t("see_sync") : t("see_async")}
        </SButton>
      )}
      {isLectureLive(lecture) ? (
        <Container fluid className="live-dashboard">
          <Row className="monitor">
            <Col className="d-flex flex-column">
              <h3>{t("sync.title")}</h3>
              <p className="text-justified">{t("sync.description")}</p>
              <div className="feedback-chart">
                <SyncLineChart />
              </div>
            </Col>
          </Row>
          <Row className="rest">
            <Col lg={2} sm={6}>
              <b>{t("sync.donut_title")}</b>
              <p>{t("sync.donut_description")}</p>
            </Col>
            <Col lg={4} sm={6} className="h-100">
              <SyncDonutGraph />
            </Col>
            <Col lg={6} sm={12} className="mt-2">
              <Row>
                <Col sm={6}>
                  <InfoCard>
                    <div className="d-flex flex-column">
                      <span className="number">{participants.students?.length || 0}</span>
                      <span className="title">{t("sync.students")}</span>
                    </div>
                    <div className="d-flex flex-row justify-content-center align-items-center">
                      <i className="fas fa-users icon" />
                    </div>
                  </InfoCard>
                </Col>
                <Col sm={6}>
                  <InfoCard>
                    <div className="d-flex flex-column">
                      <span className="number">{Math.round(currentPercentage)} %</span>
                      <span className="title">{t("sync.current_percentage")}</span>
                    </div>
                    <div className="d-flex flex-row justify-content-center align-items-center">
                      <i className="fas fa-percentage icon" />
                    </div>
                  </InfoCard>
                </Col>
                <Col sm={6}>
                  <InfoCard>
                    <div className="d-flex flex-column">
                      <span className="number">{Object.values(chatMessages).length || 0}</span>
                      <span className="title">{t("sync.messages")}</span>
                    </div>
                    <div className="d-flex flex-row justify-content-center align-items-center">
                      <i className="fas fa-comment-dots icon" />
                    </div>
                  </InfoCard>
                </Col>
                <Col sm={6}>
                  <InfoCard>
                    <div className="d-flex flex-column">
                      <span className="number">{notes?.length || 0}</span>
                      <span className="title">{t("sync.notes")}</span>
                    </div>
                    <div className="d-flex flex-row justify-content-center align-items-center">
                      <i className="fas fa-book icon" />
                    </div>
                  </InfoCard>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      ) : (
        <div className="async-dashboard">
          {seeAsyncDashboard ? (
            <div className="d-flex flex-column h-100">
              <h3 className="text-center">{t("async.first_title")}</h3>
              <p className="text-justified">{t("async.first_description")}</p>
              {bookmarks.length > 0 && (
                <div className="d-flex flex-row justify-content-center">
                  <SButton onClick={() => setViewChapters((c) => !c)}>
                    {viewChapters ? t("async.view_time") : t("async.view_chapters")}
                  </SButton>
                </div>
              )}
              <div className="feedback-chart">
                <AsyncBarChart chapterize={viewChapters} />
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column h-100">
              <h3 className="text-center">{t("async.live")}</h3>
              <p className="text-justified">{t("async.second_description")}</p>
              <div className="feedback-chart">
                <AsyncLineChart />
              </div>
            </div>
          )}
        </div>
      )}
      {/* <Questions>
        <h3 className="title">Questions</h3>
        <div className="d-flex flex-row w-100">
          <div className="d-flex flex-column justify-content-center">
            <i className={`${prevClass} far fa-arrow-alt-circle-left fs-1`} onClick={prevQuestion} />
          </div>
          <div className="question">
            {questionIndex >= 0 && questionIndex < questions.length && (
              <>
                <b>
                  {questions[questionIndex].sender.firstName} {questions[questionIndex].sender.lastName}
                </b>
                <p className="fs-5">{questions[questionIndex].text}</p>
              </>
            )}
          </div>
          <div className="d-flex flex-column justify-content-center">
            <i className={`${nextClass} far fa-arrow-alt-circle-right fs-1`} onClick={nextQuestion} />
          </div>
        </div>
      </Questions> */}
    </SDashboard>
  );
};

export default Dashboard;
